// Morphological analyser: form → every possible analysis, built from the engine's own tables.
// Powers the Word Explorer, the sentence checker and the story level checks.

import type {
    Case,
    FormStatus,
    Gender,
    KridantaKind,
    Num,
    NounEntry,
    Person,
    RootClass,
    StemClass,
    Tense,
    VerbEntry
} from '@/prakrit/lib/grammar/types'

import { cleanToken, nfc } from '@/prakrit/lib/grammar/devanagari'
import { kitNouns, kitVerbs } from '@/prakrit/lib/grammar/build'
import { PRONOUNS, PRONOUN_META } from '@/prakrit/lib/grammar/pronouns'
import { AS_DHATU } from '@/prakrit/lib/grammar/verbs'
import { CASES, NUMBERS, PERSONS, PRONOUN_CASES } from '@/prakrit/lib/grammar/types'
import {
    AVYAYA,
    EGA_FORMS,
    IRREGULAR_EN,
    IRREGULAR_GERUNDS,
    IRREGULAR_PAST_PARTICIPLES,
    NUMBERS_LIST,
    avyayaSurfaces
} from '@/prakrit/lib/grammar/lexicon'

interface Base {
    /** Review key of this exact form (or entry) — see src/prakrit/lib/content/review-keys.ts. */
    key: string
    form: string
    hi: string
    en: string
}

export interface VerbAnalysis extends Base {
    pos: 'verb'
    lemma: string
    rootClass: RootClass
    tense: Tense
    person: Person
    number: Num
    status: FormStatus
    rule?: string
}

export interface AsAnalysis extends Base {
    pos: 'as'
    lemma: 'अस'
    tense: 'vartaman' | 'bhoot'
    person: Person
    number: Num
    status: FormStatus
    negative?: boolean
}

export interface KridantaAnalysis extends Base {
    pos: 'kridanta'
    lemma: string
    rootClass: RootClass
    kind: KridantaKind
    status: FormStatus
    rule?: string
}

export interface NounAnalysis extends Base {
    pos: 'noun'
    lemma: string
    gender: Gender
    stemClass: StemClass
    case: Case
    number: Num
    status: FormStatus
    rule?: string
}

export interface PronounAnalysis extends Base {
    pos: 'pronoun'
    lemma: string
    base: string
    slug: string
    gender: Gender | null
    person: Person
    case: Exclude<Case, 'sambodhan'>
    number: Num
    status: FormStatus
    rule?: string
}

export interface AvyayaAnalysis extends Base {
    pos: 'avyaya'
    lemma: string
}

export interface NumberAnalysis extends Base {
    pos: 'number'
    lemma: string
    value: number
    gender?: Gender
}

export interface IrregularAnalysis extends Base {
    pos: 'irregular'
    lemma: string
    kind: 'bhoot_kridanta' | 'sambandh_kridanta'
    root?: string
}

/** The bare root itself (धातु), e.g. पढ. */
export interface RootAnalysis extends Base {
    pos: 'dhatu'
    lemma: string
    rootClass: RootClass | 'as'
}

/** The bare noun stem itself (मूल शब्द), e.g. पुरिस. */
export interface StemAnalysis extends Base {
    pos: 'stem'
    lemma: string
    gender: Gender
    stemClass: StemClass
}

export type Analysis =
    | RootAnalysis
    | StemAnalysis
    | VerbAnalysis
    | AsAnalysis
    | KridantaAnalysis
    | NounAnalysis
    | PronounAnalysis
    | AvyayaAnalysis
    | NumberAnalysis
    | IrregularAnalysis

export type FormIndex = Map<string, Analysis[]>

const add = (index: FormIndex, a: Analysis) => {
    const key = nfc(a.form)
    const list = index.get(key)

    if (list) list.push(a)
    else index.set(key, [a])
}

export const indexVerb = (index: FormIndex, v: VerbEntry): void => {
    add(index, { pos: 'dhatu', key: `lex|dhatu|${v.root}`, form: v.root, lemma: v.root, rootClass: v.class, hi: v.hi, en: v.en })

    for (const [tense, tab] of Object.entries(v.forms)) {
        if (tense === 'kridanta') {
            for (const [kind, cell] of Object.entries(v.forms.kridanta)) {
                for (const f of cell ?? []) {
                    add(index, {
                        pos: 'kridanta',
                        key: `kf|${v.root}|${kind}|${f.form}`,
                        form: f.form,
                        lemma: v.root,
                        rootClass: v.class,
                        kind: kind as KridantaKind,
                        status: f.status,
                        rule: f.rule,
                        hi: v.hi,
                        en: v.en
                    })
                }
            }
            continue
        }

        const grid = tab as VerbEntry['forms']['vartaman']

        if (!grid) continue

        for (const p of PERSONS) {
            for (const n of NUMBERS) {
                for (const f of grid[p][n]) {
                    add(index, {
                        pos: 'verb',
                        key: `vf|${v.root}|${tense}|${p}|${n}|${f.form}`,
                        form: f.form,
                        lemma: v.root,
                        rootClass: v.class,
                        tense: tense as Tense,
                        person: p,
                        number: n,
                        status: f.status,
                        rule: f.rule,
                        hi: v.hi,
                        en: v.en
                    })
                }
            }
        }
    }
}

export const indexNoun = (index: FormIndex, n: NounEntry): void => {
    add(index, { pos: 'stem', key: `lex|noun|${n.stem}`, form: n.stem, lemma: n.stem, gender: n.gender, stemClass: n.stem_class, hi: n.hi, en: n.en })

    for (const c of CASES) {
        for (const num of NUMBERS) {
            for (const f of n.forms[c][num]) {
                add(index, {
                    pos: 'noun',
                    key: `nf|${n.stem}|${c}|${num}|${f.form}`,
                    form: f.form,
                    lemma: n.stem,
                    gender: n.gender,
                    stemClass: n.stem_class,
                    case: c,
                    number: num,
                    status: f.status,
                    rule: f.rule ?? n.rules,
                    hi: n.hi,
                    en: n.en
                })
            }
        }
    }
}

const buildIndex = (extraVerbs: VerbEntry[] = [], extraNouns: NounEntry[] = []): FormIndex => {
    const index: FormIndex = new Map()

    for (const v of [...kitVerbs(), ...extraVerbs]) indexVerb(index, v)
    for (const n of [...kitNouns(), ...extraNouns]) indexNoun(index, n)

    for (const meta of PRONOUN_META) {
        const tab = PRONOUNS[meta.key]

        for (const c of PRONOUN_CASES) {
            for (const num of NUMBERS) {
                for (const f of tab[c][num]) {
                    add(index, {
                        pos: 'pronoun',
                        key: `pf|${meta.key}|${c}|${num}|${f.form}`,
                        form: f.form,
                        lemma: meta.key,
                        base: meta.base,
                        slug: meta.slug,
                        gender: meta.gender,
                        person: meta.person,
                        case: c,
                        number: num,
                        status: f.status,
                        rule: f.rule,
                        hi: meta.hi,
                        en: meta.en
                    })
                }
            }
        }
    }

    for (const tense of ['vartaman', 'bhoot'] as const) {
        for (const p of PERSONS) {
            for (const num of NUMBERS) {
                for (const f of AS_DHATU[tense][p][num]) {
                    add(index, {
                        pos: 'as',
                        key: `vf|अस|${tense}|${p}|${num}|${f.form}`,
                        form: f.form,
                        lemma: 'अस',
                        tense,
                        person: p,
                        number: num,
                        status: f.status,
                        hi: AS_DHATU.hi,
                        en: AS_DHATU.en
                    })
                }
            }
        }
    }

    add(index, { pos: 'dhatu', key: 'lex|dhatu|अस', form: 'अस', lemma: 'अस', rootClass: 'as', hi: AS_DHATU.hi, en: AS_DHATU.en })

    // णत्थि = ण + अत्थि (negative of अत्थि)
    add(index, {
        pos: 'as',
        key: 'vf|अस|vartaman|anya|eka|णत्थि',
        form: 'णत्थि',
        lemma: 'अस',
        tense: 'vartaman',
        person: 'anya',
        number: 'eka',
        status: 'core',
        negative: true,
        hi: 'नहीं है',
        en: 'is not'
    })

    for (const [entry, hi, en] of AVYAYA) {
        for (const surface of avyayaSurfaces(entry)) {
            add(index, { pos: 'avyaya', key: `lex|avyaya|${entry}`, form: surface, lemma: entry, hi, en })
        }
    }

    for (const [word, value] of NUMBERS_LIST) {
        add(index, { pos: 'number', key: `lex|number|${word}`, form: word, lemma: word, value, hi: String(value), en: String(value) })
    }

    for (const { form, gender } of EGA_FORMS) {
        add(index, { pos: 'number', key: `lex|number|${form}`, form, lemma: 'एग', value: 1, gender, hi: 'एक', en: 'one' })
    }

    for (const [form, root, hi] of IRREGULAR_PAST_PARTICIPLES) {
        add(index, { pos: 'irregular', key: `lex|irregular|${form}`, form, lemma: form, kind: 'bhoot_kridanta', root, hi, en: IRREGULAR_EN[form] ?? hi })
    }

    for (const [form, hi] of IRREGULAR_GERUNDS) {
        add(index, { pos: 'irregular', key: `lex|irregular|${form}`, form, lemma: form, kind: 'sambandh_kridanta', hi, en: IRREGULAR_EN[form] ?? hi })
    }

    return index
}

let defaultIndex: FormIndex | null = null

export const formIndex = (): FormIndex => {
    if (!defaultIndex) defaultIndex = buildIndex()

    return defaultIndex
}

/** Rebuild the index with editor-added verbs and nouns (server side, after a lexicon change). */
export const rebuildFormIndex = (extraVerbs: VerbEntry[], extraNouns: NounEntry[]): FormIndex => {
    defaultIndex = buildIndex(extraVerbs, extraNouns)

    return defaultIndex
}

/** Every analysis of one token. Punctuation is ignored; the lookup is NFC-normalised. */
export const analyse = (token: string, index: FormIndex = formIndex()): Analysis[] => index.get(cleanToken(token)) ?? []

export const isFinite = (a: Analysis): a is VerbAnalysis | AsAnalysis => a.pos === 'verb' || a.pos === 'as'

/** Is this analysis the nominative of a noun, pronoun or number word? */
export const isNominative = (a: Analysis): boolean => (a.pos === 'noun' || a.pos === 'pronoun') && a.case === 'prathama'

export const personOf = (a: Analysis): Person => (a.pos === 'pronoun' ? a.person : 'anya')

export const numberOf = (a: Analysis): Num | null => {
    if ('number' in a && typeof a.number === 'string') return a.number
    if (a.pos === 'number') return a.value === 1 ? 'eka' : 'bahu'

    return null
}

/** Count of distinct forms in the index — shown on /about/content. */
export const indexSize = (index: FormIndex = formIndex()): number => index.size
